import ClientService, { getHemisphereSessionToken, notifyHemisphereSessionExpired } from './client-service'

interface AddAuthorizationParams {
  email: string
  name: string
  expiration_date?: number
  hemis_level: string
  activated?: boolean
}
class HemisphereService extends ClientService {
  constructor() {
    super(process.env.VUE_APP_HEMISPHERE_URL, {
      headerProvider() {
        const headers: any = {
          'Content-Type': 'application/json'
        }
        const token = getHemisphereSessionToken()
        if (token) {
          headers['Authorization'] = 'Bearer ' + token
        }
        return headers
      },
      deconnectionHandler() {
        notifyHemisphereSessionExpired()
      }
    })
  }
  signup(user: any) {
    return this.client.post('/users', {
      email: user.email,
      first_name: user.firstname,
      last_name: user.lastname,
      password: user.password
    })
  }

  updateUser(userData: any) {
    return this.client.put('/users/me/infos', userData)
  }

  sendUserPicture(picture: any) {
    return this.client.post(`/users/me/photo`, picture, {
      headers: {
        'Content-type': 'multipart/form-data'
      }
    })
  }

  deleteUserPicture() {
    return this.client.delete('/users/me/photo')
  }

  updateEmail(newMail: string) {
    const params = new URLSearchParams()
    params.append('new_mail', newMail)
    return this.client.post('/users/me/email', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  updatePasswordManual(oldPassword: string, newPassword: string) {
    const params = new URLSearchParams()
    params.append('old_password', oldPassword)
    params.append('new_password', newPassword)
    return this.client.post('/users/me/password', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  forgottenPassword(email: string) {
    const params = new URLSearchParams()
    params.append('email', email)
    return this.client.post('/users/user/forgotten', null, {
      params: params,
      headers: {
        'X-Service-Id': process.env.VUE_APP_APP_NAME
      }
    })
  }

  async resetPassword(newPassword: string, hashKey: string): Promise<any> {
    try {
      const params = new URLSearchParams()
      params.append('password', newPassword)
      params.append('hash_key', hashKey)
      await this.client.post('/users/password/change', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
      //empty response from server
      return 'ok'
    } catch (error) {
      return this.handleError(error as Error, undefined)
    }
  }

  setBuildingPicture(buildingId: string, picture: File) {
    return this.client.post(
      `/buildings/${buildingId}/photo`,
      { photo: picture },
      {
        headers: {
          'Content-type': 'multipart/form-data'
        }
      }
    )
  }

  addBuilding(building: any) {
    return this.client.post('/buildings', building)
  }

  updateBuilding(building: any, buildingId: string) {
    return this.client.put(`/buildings/${buildingId}`, building)
  }

  addBuildingToProject(building: any, p_id: string) {
    return this.client.post('/buildings', building, { params: { p_id } })
  }

  getBuilding(buildingId: string) {
    return this.client.get(`/buildings/${buildingId}`)
  }

  getBuildingStatus(buildingId: string) {
    return this.client.get(`/buildings/${buildingId}/instance/status`)
  }

  addOperation(operation: any) {
    return this.client.post('/projects', operation)
  }

  getBuildingsById(idArray: string[]) {
    return this.client.post('/buildings/ids', idArray)
  }

  getProjects(pagination: string, pageNumber: string, root: string) {
    const params = new URLSearchParams()
    if (pagination != null) {
      params.append('pagination', pagination)
    }
    if (pageNumber != null) {
      params.append('page', pageNumber)
    }
    if (root != null) {
      params.append('root', root)
    }

    return this.client.get('/entities', { params: params })
  }

  getProject(projectId: string) {
    return this.client.get(`/projects/${projectId}`)
  }

  getSubGroups(entityId: string) {
    return this.client.get(`/entities/${entityId}/subgroups`)
  }

  editSubGroup(id: string, subgroup: any) {
    return this.client.put('/entities/' + id, subgroup)
  }

  deleteSubGroup(id: string) {
    return this.client.delete('/entities/' + id)
  }

  addSubGroup(parent_id: string, subgroup: any) {
    return this.client.post('/entities/' + parent_id + '/subgroups', subgroup)
  }

  getBuildingsProject(projectId: string, cancelSource: any) {
    if (cancelSource !== null) {
      return this.client.get(`/projects/${projectId}/buildings`, {
        cancelToken: cancelSource.token
      })
    } else {
      return this.client.get(`/projects/${projectId}/buildings`)
    }
  }

  getBuildingsMap(minLat: number, maxLat: number, minLong: number, maxLong: number) {
    return this.client.get('addresses?minLat=' + minLat + '&maxLat=' + maxLat + '&minLong=' + minLong + '&maxLong=' + maxLong)
  }

  getMyUserInfos() {
    return this.client.get('/users/me/infos')
  }

  getMyAuthorizations() {
    return this.client.get('/buildings/authorizations/user')
  }

  getUserLicences(userId: string) {
    return this.client.get(`/users/${userId}/licences`)
  }

  getUserSubscriptions(userId: string) {
    return this.client.get(`/users/${userId}/subscriptions`)
  }

  getServers() {
    return this.client.get(`/servers`)
  }

  getServer(serverId: string) {
    return this.client.get(`/servers/${serverId}`)
  }

  getServersContainers(serverId: string) {
    return this.client.get(`/servers/${serverId}/containers`)
  }
  getContainersKernels(containerId: string) {
    return this.client.get(`/containers/${containerId}?buildings=true`)
  }
  globalSearch(query: string) {
    return this.client.get(`/global/search?query=${query}`)
  }
  getBuildingAuthorizations(buildingId: string) {
    return this.client.get(`/buildings/${buildingId}/authorizations`)
  }

  addBuildingAuthorizations(buildingId: string, params: AddAuthorizationParams) {
    const queryParams = new URLSearchParams()
    queryParams.append('email', params.email)
    queryParams.append('name', params.name)
    queryParams.append('hemis_level', params.hemis_level)
    if (params.expiration_date) {
      queryParams.append('expiration_date', params.expiration_date.toString())
    }
    if (params.activated) {
      queryParams.append('activated', params.activated.toString())
    }
    return this.client.post(`/buildings/${buildingId}/authorizations`, queryParams, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  getUsersOfBuildingsInGroupEntity(parent_group_entity: string) {
    return this.client.get(`/buildings/${parent_group_entity}/users`)
  }

  async activateAuthorization(userId: string, hashKey: string, authorizationId: string): Promise<any> {
    const payload = {
      user_id: userId,
      hash_key: hashKey
    }
    try {
      const { data } = await this.client.put(`/buildings/authorizations/${authorizationId}/activate`, payload)
      return data
    } catch (error) {
      return this.handleError(error as Error, undefined)
    }
  }

  async validateUserMail(userId: string, hashKey: string) {
    try {
      return await this.client.get(`/users/${userId}/verify/${hashKey}`)
    } catch (error) {
      return this.handleError(error as Error, undefined)
    }
  }

  async revokeUserCompany(userId: string, hashKey: string) {
    try {
      const params = new URLSearchParams()
      params.append('hash', hashKey)
      return await this.client.put(`/users/${userId}/revoke-company`, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
    } catch (error) {
      return this.handleError(error as Error, undefined)
    }
  }
}

const hemisphereService = new HemisphereService()

export default hemisphereService
